import { useState } from 'react'
import toast from 'react-hot-toast'
import { authService } from '@/services/authService'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useTranslation } from '@/i18n/useTranslation'

type Props = {
  challengeId: string
  onSuccess: (data: {
    token: string
    user: {
      id: number
      email: string
      name: string
      role?: string
      shop_id?: number
      is_web_user?: boolean
      customer_code?: string
      branch_access?: 'all' | number[]
      primary_branch_id?: number | null
    }
    expires_in?: number
    idle_timeout_minutes?: number
    max_session_expires_at?: string
  }) => void
  onCancel?: () => void
}

export function TwoFactorLoginForm({ challengeId, onSuccess, onCancel }: Props) {
  const [token, setToken] = useState('')
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation('common')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const res = await authService.verifyTwoFactorLogin({ challenge_id: challengeId, token })
      if (res.success && res.data) {
        onSuccess(res.data)
      } else {
        toast.error(res.message || 'Invalid code')
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined
      toast.error(msg || 'Invalid code')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-600">
        Enter the 6-digit code from your authenticator app, or a recovery code.
      </p>
      <div>
        <label htmlFor="mfa-token" className="form-label">
          Authentication code
        </label>
        <input
          id="mfa-token"
          className="input"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          autoComplete="one-time-code"
          inputMode="numeric"
          placeholder="123456"
          required
        />
      </div>
      <div className="flex gap-3">
        <button type="submit" className="btn btn-primary flex-1" disabled={loading}>
          {loading ? <LoadingSpinner size="sm" /> : t('auth.signIn')}
        </button>
        {onCancel && (
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
            {t('common.actions.cancel')}
          </button>
        )}
      </div>
    </form>
  )
}
